import transformer from '@utils/transformer'
import pad from '@utils/pad'
import createHttpClient, { createAbortController } from '@services/http/client'
import ReconnectingWebSocket from 'ReconnectingWebSocket'
import Emitter from 'component-emitter'
import debounce from 'lodash/debounce'
import keyBy from 'lodash/keyBy'

const Promise = require('bluebird')

let cancelRequest

export default function apiCreator(storage, url = process.env.VUE_APP_API_BASE_URL) {
    let authorization = ''

    const httpClient = createHttpClient({
        baseUrl: url,
        timeout: 10000,
        headers: {
            'Content-Type': 'application/json',
        },
        getToken: () => authorization,
    })

    const handleSchedule = items => {
        return items.map(el => ({
            ...el,
            mix_id: el.mixes_id && el.mixes_id.id,
            duration: el.duration * 1000,
            scheduled_start_time: new Date(el.scheduled_start_time).getTime(),
            scheduled_end_time: new Date(el.scheduled_end_time).getTime(),
            id: el.id,
        }))
    }

    const handleShowSchedule = (items, channelId) => {
        return items.map(el => ({
            ...el,
            station: channelId,
            id: el.id ? el.id : `${el.artist_id && el.artist_id.id}${el.show_id && el.show_id.id}${el.air_start}`,
            air_start: new Date(el.air_start).getTime(),
            air_end: new Date(el.air_end).getTime(),
        }))
    }

    const resourceList = [
        {
            name: 'mixes',
            modelName: 'Mixes',
            type: 'mix',
        },
        {
            name: 'shows',
            modelName: 'Shows',
            type: 'show',
        },
        {
            name: 'artists',
            modelName: 'Artists',
            type: 'artist',
        },
        {
            name: 'episodes',
            modelName: 'Episodes',
            type: 'episode',
        },
    ]

    const joinResources = function (data) {
        for (var i = resourceList.length - 1; i >= 0; i--) {
            const currentResource = resourceList[i]
            if (data[currentResource.modelName]) {
                storage[currentResource.name].join(
                    data[currentResource.modelName].map(el => ({ ...el, type: currentResource.type }))
                )
            }
        }
    }

    const resource = function ({ name, modelName }) {
        const fetchQueue = []
        const events = Emitter({})
        let queueIterator = 0 // match each queue to a number, used for events
        const fetchPostpone = function (idObj) {
            // have this function add to fetchQueue
            // have other function attempt to fetch all in queue or get debounced
            fetchQueue.push(idObj)
            const promise = new Promise(resolve => {
                events.on(queueIterator, evt => {
                    resolve(evt[idObj.id])
                })
            })
            fetchDebounced()
            return promise
        }

        const fetchDebounced = debounce(() => {
            // multiple instances of fetch needed due to url length limitations

            const fetchMax = 10
            const fetchCount = Math.ceil(fetchQueue.length / fetchMax)
            for (let i = 0; i < fetchCount; i++) {
                const fetchString = `v3/dispatch?models=${JSON.stringify(
                    fetchQueue.slice(i * fetchMax, (i + 1) * fetchMax)
                )}&depth=2`
                const eventId = queueIterator

                httpClient.get(fetchString).then(
                    ({ body }) => {
                        events.emit(
                            eventId,
                            keyBy(body[modelName], o => o.id)
                        )
                        joinResources(body)
                    },
                    () => []
                )
            }

            fetchQueue.length = 0
            queueIterator++
        })

        return {
            get(idObj) {
                return fetchPostpone(idObj)
            },
            getTrending(channel) {
                return httpClient
                    .get(`v3/${name}?channel=${channel}&trending=true&offset=0&limit=6`)
                    .then(({ body }) => {
                        return body
                    })
            },
        }
    }

    const resources = resourceList.reduce((acc, item) => {
        acc[item.name] = resource(item)
        return acc
    }, {})

    resources.mixes.getArtistTrending = function (id, limit = 5, offset = 0) {
        return httpClient
            .get(`v3/mixes?artists_id=${id}&limit=${limit}&offset=${offset}&order=-reach`)
            .then(({ body }) => {
                return body
            })
    }

    resources.mixes.getArtistLatest = function (id, limit = 5, offset = 0) {
        return httpClient
            .get(`v3/mixes?artists_id=${id}&limit=${limit}&offset=${offset}&order=-creation_time`)
            .then(({ body }) => {
                return body
            })
    }

    resources.mixes.getGenreLatest = function (genreTitle, limit = 12, offset = 0) {
        return httpClient
            .get(`v3/mixes?genre=${encodeURI(genreTitle)}&limit=${limit}&offset=${offset}&order=-creation_time`)
            .then(({ body }) => {
                return body
            })
    }

    resources.mixes.getGenreTrending = function (genreTitle, limit = 12, offset = 0) {
        return httpClient
            .get(`v3/mixes?genre=${encodeURI(genreTitle)}&limit=${limit}&offset=${offset}&order=-reach`)
            .then(({ body }) => {
                return body
            })
    }

    resources.artists.getGenreRandom = function (genreTitle, limit = 12, offset = 0) {
        return httpClient
            .get(
                `v3/artists?genre=${encodeURI(genreTitle)}&limit=${limit}&offset=${offset}&order=rand()
`
            )
            .then(({ body }) => {
                return body
            })
    }

    const channels = {
        get() {
            return httpClient.get('v3/stations').then(
                ({ body }) => {
                    return Object.values(body)
                        .map(el => transformer.channel(el))
                        .sort((a, b) => (a.title > b.title ? 1 : b.title > a.title ? -1 : 0))
                },
                () => []
            )
        },
    }

    const nowPlaying = (function () {
        let socket = null
        const events = Emitter({})
        let nowPlayingData = ''
        let forceSend = true

        function socketHandler(e) {
            const newNowPlayingData = e.data
            if (forceSend || newNowPlayingData !== nowPlayingData) {
                forceSend = false
                nowPlayingData = newNowPlayingData
                return events.emit('dataUpdate', JSON.parse(nowPlayingData))
            }
            return false
        }

        return {
            get() {
                if (!socket) {
                    socket = new ReconnectingWebSocket(process.env.VUE_APP_API_NOW_PLAYING, null, {
                        reconnectInterval: 5000,
                    })
                    socket.addEventListener('message', socketHandler)
                    socket.addEventListener('open', () => {
                        forceSend = true
                    })
                }
                return events
            },
        }
    })()

    const schedule = {
        get(id = null, offset = 0, range = process.env.VUE_APP_PLAYLIST_ITEMS_INIT, time = new Date()) {
            const schedulePromise = httpClient
                .get(
                    `v3/stations/playlists?${id ? `station=${id}&` : ``}after=${time.getUTCFullYear()}-${pad(
                        time.getUTCMonth() + 1
                    )}-${pad(time.getUTCDate())}T${pad(time.getUTCHours())}:00:00&offset=${offset}&limit=${
                        id ? range : range * 4
                    }`
                )
                .then(
                    ({ body }) => handleSchedule(body),
                    () => []
                )
            return schedulePromise
        },
    }

    const showSchedule = {
        get(startDate = null, endDate = null, channelId = null) {
            // https://api.dev.frisky.fm/v3/episodes?startdate=2019-03-12T00:00:00&enddate=2019-03-12T23:59:59&channel=deep
            const schedulePromise = httpClient
                .get(
                    `v3/episodes?startdate=${startDate.getUTCFullYear()}-${pad(startDate.getUTCMonth() + 1)}-${pad(
                        startDate.getUTCDate()
                    )}T${pad(startDate.getUTCHours())}:00:00&enddate=${endDate.getUTCFullYear()}-${pad(
                        endDate.getUTCMonth() + 1
                    )}-${pad(endDate.getUTCDate())}T${pad(endDate.getUTCHours())}:00:00${
                        channelId ? `&channel=${channelId}` : ``
                    }`
                )
                .then(
                    ({ body }) => handleShowSchedule(body, channelId),
                    () => []
                )
            return schedulePromise
        },
    }

    const login = function ({ email, password }) {
        return httpClient.post('v3/auth/token', { email, password })
    }

    const signup = function ({ data }) {
        return httpClient.post('v3/members', data)
    }

    const revokeToken = () => httpClient.post('v3/auth/token/revoke')

    const resetPasswordRequest = function ({ email }) {
        return httpClient.post('v3/members/reset-password' + '/?email=' + email, { email: email })
    }

    const resetPassword = function ({ member, token }) {
        return httpClient.put(`v3/members/${member.id}`, member, {
            headers: { Authorization: 'Bearer ' + token },
        })
    }

    const favMixes = {
        get(membersId) {
            return httpClient.get(`v3/members/${membersId}/favorites/mixes?limit=100000`)
        },
        post(membersId, content) {
            return httpClient.post(`v3/members/${membersId}/favorites/mixes`, content)
        },
        delete(membersId, id) {
            return httpClient.delete(`v3/members/${membersId}/favorites/mixes/${id}`)
        },
    }

    const favArtists = {
        get(membersId) {
            return httpClient.get(`v3/members/${membersId}/favorites/artists?limit=100000`)
        },
        post(membersId, content) {
            return httpClient.post(`v3/members/${membersId}/favorites/artists`, content)
        },
        delete(membersId, id) {
            return httpClient.delete(`v3/members/${membersId}/favorites/artists/${id}`)
        },
    }

    const favShows = {
        get(membersId) {
            return httpClient.get(`v3/members/${membersId}/favorites/shows?limit=100000`)
        },
        post(membersId, content) {
            return httpClient.post(`v3/members/${membersId}/favorites/shows`, content)
        },
        delete(membersId, mixId) {
            return httpClient.delete(`v3/members/${membersId}/favorites/shows/${mixId}`)
        },
    }

    const genres = {
        getAll() {
            return httpClient.get(`v3/genres`).then(response => {
                return { body: response.body }
            })
        },
    }

    const search = {
        get(keyword, limit = 6, offset = 0) {
            if (cancelRequest !== undefined) {
                cancelRequest()
            }
            const controller = createAbortController()
            cancelRequest = () => controller.abort()

            return httpClient
                .get(`v3/search?query=${encodeURIComponent(keyword)}&limit=${limit}&offset=${offset}`, {
                    signal: controller.signal,
                })
                .then(response => {
                    return { headers: response.headers, body: response.body }
                })
        },
    }

    const token = {
        set(token) {
            authorization = token
        },
        get() {
            return authorization
        },
    }

    return {
        ...resources,
        schedule,
        showSchedule,
        channels,
        nowPlaying,
        favMixes,
        favArtists,
        favShows,
        genres,
        search,
        login,
        signup,
        revokeToken,
        resetPasswordRequest,
        resetPassword,
        token,
        get: httpClient.get,
    }
}
